import React, { FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { ISanityImage } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { IRewardFragment, LoyaltyTierKey } from 'generated/graphql-gateway';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import useMediaQuery from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { RuleType } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { EVENT_PAGE_NAMES } from 'state/mParticle/constants';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import {
  CarouselRewardLock,
  ItemLoyaltyIcon,
  RewardPictureLock,
} from '../loyalty-rewards/loyalty-rewards.styled';

import {
  CardWrapper,
  CarouselPoints,
  CarouselRewardDescription,
  CarouselRewardItem,
  CarouselRewardName,
  CarouselSectionRewards,
  LastCarouselItem,
  LastItemContent,
  LastItemWrapper,
  LockOverlay,
  PictureWrapper,
  StyledRewardsCarousel,
} from './loyalty-rewards-carousel.styled';
import LoyaltyRewardsUnauthModal from './loyalty-rewards-unauth-modal';
import { IBannerReward, ILoyaltyRewardsCarouselProps, RewardItem } from './types';

export const LoyaltyRewardsCarousel: FC<ILoyaltyRewardsCarouselProps> = ({
  rewards,
  currentUserTier,
  categorySectionId,
  categoryHeaderId,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setEventPageName } = useLocationContext();
  const isMobile = useMediaQuery('mobile');
  const { engineRewardsMap, sanityRewardsMap } = useLoyaltyRewardsList();
  const { getContentForTier } = useFeaturesLoyaltyTiersContent();
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();
  const mParticle = useMParticleContext();

  const { loyaltyUser } = useLoyaltyUser();
  const { isRewardTierLocked } = useLoyaltyRewards(loyaltyUser);
  const userPoints = loyaltyUser?.points || 0;

  const [shouldRenderModal, setShouldRenderModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<IBannerReward | null>(null);

  const rewardLabel = useCallback(
    (engineReward: IRewardFragment) => {
      // If reward has Loyalty Tier requirement and user tier is different show required tier in label
      if (isRewardTierLocked(engineReward, currentUserTier)) {
        return getContentForTier(engineReward.requiredLoyaltyTier!)?.loyaltyTiersUIConfigName
          ?.locale;
      }

      // If reward does not have points, it's free
      if (!engineReward?.pointCost) {
        return formatMessage({ id: 'loyaltyFree' });
      }

      return engineReward?.pointCost?.toString();
    },
    [currentUserTier, formatMessage, getContentForTier, isRewardTierLocked]
  );

  const sendCardEvent = useCallback(
    (eventName: string, rewardId?: string, rewardLocked?: boolean) => {
      mParticle.trackEvent({
        name: CustomEventNames.CARD,
        type: EventTypes.Other,
        attributes: {
          Name: eventName,
          Path: location.pathname,
          Type: rewardId,
          Status: rewardLocked ? 'Locked' : 'Unlocked',
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mParticle]
  );

  const handleUnauthRewardClick = useCallback(
    (reward: IBannerReward, rewardLocked: boolean) => {
      if (isAuthenticated()) {
        return;
      }
      sendCardEvent('Reward', reward._id, rewardLocked);
      setEventPageName(EVENT_PAGE_NAMES.RewardDetail);
      setSelectedReward(reward);
      setShouldRenderModal(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  );

  const unauthModalDismiss = useCallback(() => {
    setSelectedReward(null);
    setShouldRenderModal(false);
  }, []);

  const handleRewardClick = useCallback(
    (rewardId: string, rewardLocked: boolean) => {
      sendCardEvent('Reward', rewardId, rewardLocked);
      setEventPageName(EVENT_PAGE_NAMES.RewardDetail);
      navigate(`${routes.rewardsList}/${rewardId}`, { state: { backRoute: pathname } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, pathname]
  );

  const handleViewMoreClick = useCallback(() => {
    sendCardEvent('View More Rewards');
    navigate(routes.rewardsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, mParticle]);

  const Item = ({
    rewardId,
    rewardLocked,
    image,
    imageAlt,
    rewardName,
    rewardDescription,
    label,
    onClick,
  }: RewardItem) => {
    return (
      <CarouselRewardItem key={rewardId} onClick={onClick}>
        <CardWrapper locked={rewardLocked}>
          <PictureWrapper>
            {image && <Picture image={image} alt={imageAlt || ''} objectFitContain={true} />}
            {rewardLocked && <LockOverlay />}
            {rewardLocked && <RewardPictureLock fill={primitive.$white} />}
          </PictureWrapper>

          <CarouselRewardName>{rewardName}</CarouselRewardName>

          {label === formatMessage({ id: 'loyaltyFree' }) ? (
            <CarouselRewardDescription>
              {rewardDescription?.[0].children?.[0].text}
            </CarouselRewardDescription>
          ) : null}

          <CarouselPoints locked={rewardLocked}>
            {rewardLocked ? <CarouselRewardLock /> : <ItemLoyaltyIcon />}
            <span>{label}</span>
          </CarouselPoints>
        </CardWrapper>
      </CarouselRewardItem>
    );
  };

  const carouselCardMapper = (reward: IBannerReward) => {
    const rewardId = reward?._id;
    const incentive = sanityRewardsMap?.[rewardId || ''];

    const loyaltyRewardId = incentive?.loyaltyEngineId || '';
    const engineReward = engineRewardsMap[loyaltyRewardId];

    // Reward is locked when user does not have enough points or it belongs to a different tier, or not logged in
    const rewardLocked =
      !isAuthenticated() ||
      userPoints < engineReward?.pointCost ||
      isRewardTierLocked(engineReward, currentUserTier);

    if (!isAuthenticated() || !incentive) {
      const rewardPoints =
        reward?.ruleSet?.find(rule => rule.ruleSetType === RuleType.LoyaltyPoints)?.points || 0;

      const tierRequirement = reward?.ruleSet?.find(rule => rule.ruleSetType === RuleType.TierList)
        ?.tierList;

      const label = tierRequirement
        ? getContentForTier(tierRequirement as LoyaltyTierKey)?.loyaltyTiersUIConfigName?.locale
        : rewardPoints
          ? rewardPoints.toString()
          : formatMessage({ id: 'loyaltyFree' });

      return Item({
        rewardId,
        rewardLocked,
        image: reward.image?.locale,
        imageAlt: reward.imageDescription?.locale,
        rewardName: reward?.name?.locale,
        rewardDescription: reward.description?.locale,
        label,
        onClick: () => handleUnauthRewardClick(reward, rewardLocked),
      });
    }

    const incentiveData = parseIncentiveData(incentive);

    return Item({
      rewardId,
      rewardLocked,
      image: incentiveData.image as ISanityImage,
      imageAlt: incentiveData.imageDescription,
      rewardName: incentiveData.name,
      rewardDescription: reward.description?.locale,
      label: rewardLabel(engineReward),
      onClick: () => handleRewardClick(incentiveData.id, rewardLocked),
    });
  };

  const lastCarouselItem = (
    <LastCarouselItem onClick={handleViewMoreClick}>
      <LastItemWrapper>
        <LastItemContent>
          {formatMessage({ id: 'rewardsViewMore' })}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g id="arrow_forward">
              <path
                id="Vector"
                d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z"
                fill="#502314"
              />
            </g>
          </svg>
        </LastItemContent>
      </LastItemWrapper>
    </LastCarouselItem>
  );

  const carouselItems = [...rewards.map(carouselCardMapper), lastCarouselItem];

  if (isMobile) {
    return (
      <>
        <StyledRewardsCarousel>{carouselItems}</StyledRewardsCarousel>
        {shouldRenderModal && (
          <LoyaltyRewardsUnauthModal
            selectedReward={selectedReward!}
            onDismiss={unauthModalDismiss}
          />
        )}
      </>
    );
  }

  return (
    <>
      <CarouselSectionRewards
        id={categorySectionId}
        role="region"
        aria-labelledby={categoryHeaderId}
      >
        {carouselItems}
      </CarouselSectionRewards>
      {shouldRenderModal && (
        <LoyaltyRewardsUnauthModal
          selectedReward={selectedReward!}
          onDismiss={unauthModalDismiss}
        />
      )}
    </>
  );
};
